import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

interface AuthGuardProps {
  children: React.ReactNode;
}

export default function AuthGuard({ children }: AuthGuardProps) {
  const { user, loading } = useAuthStore();
  const location = useLocation();

  // Skip auth check for callback page and auth page
  if (location.pathname === '/auth/callback' || location.pathname === '/auth') {
    return <>{children}</>;
  }

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-surface mx-auto"></div>
          <p className="mt-4 text-surface">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    // Store the attempted location for redirect after login
    if (location.pathname !== '/') {
      sessionStorage.setItem('authRedirect', location.pathname);
    }
    return <Navigate to="/auth" replace />;
  }

  // Check onboarding status from localStorage and user profile
  const onboardingStatus = localStorage.getItem('onboardingStatus') || 'incomplete';
  const profileComplete = user.onboarding_completed && user.terms_accepted;
  
  // If onboarding is complete in both localStorage and profile, allow access to all routes
  if (onboardingStatus === 'complete' && profileComplete) {
    return <>{children}</>;
  }

  // Only redirect to onboarding if:
  // 1. Not in onboarding flow
  // 2. Not in auth callback page
  // 3. Not in profile page
  if (
    !location.pathname.startsWith('/onboarding') &&
    !location.pathname.startsWith('/auth/callback') &&
    location.pathname !== '/profile'
  ) {
    // Determine which onboarding step to start with based on missing fields
    let redirectPath = '/onboarding/name';
    
    if (user.full_name && user.display_name) {
      redirectPath = '/onboarding/profession';
    }
    if (user.professions?.length > 0) {
      redirectPath = '/onboarding/contact';
    }
    if (user.location) {
      redirectPath = '/onboarding/experience';
    }
    if (user.experience_level) {
      redirectPath = '/onboarding/goals';
    }
    if (user.goals) {
      redirectPath = '/onboarding/terms';
    }

    // Only redirect if we're not already on the correct path
    if (location.pathname !== redirectPath) {
      return <Navigate to={redirectPath} replace />;
    }
  }

  return <>{children}</>;
}